import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api, { portfolioAPI } from '../../services/api';
import { PortfolioComment, Portfolio, PortfolioComments } from '../../types';

import toast from 'react-hot-toast';

interface PortfolioState {
  comments: PortfolioComment[];
  commentsLoading: boolean;
  error: string | null;
  commentsPage: number;
  hasMoreComments: boolean;
  repliesPage: any;
  hasMoreReplies: any;
  repliesLoading: any;
  allPortfolios: Portfolio[],
  currentPage: number,
  hasMorePortfolios: boolean,
  loading: boolean,
  portfolioComments: PortfolioComments
}

const initialState: PortfolioState = {
  comments: [],
  commentsLoading: false,
  error: null,
  commentsPage: 1,
  hasMoreComments: true,
  repliesPage: {},
  hasMoreReplies: {},
  repliesLoading: {},
  allPortfolios: [] as Portfolio[],
  currentPage: 0,
  hasMorePortfolios: true,
  loading: false,
  portfolioComments: {}
};

// Async thunks
export const togglePortfolioLike = createAsyncThunk(
  'portfolio/toggleLike',
  async (portfolioId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await portfolioAPI.toggleLike(portfolioId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to toggle like');
    }
  }
);

export const addPortfolioComment = createAsyncThunk(
  'portfolio/addComment',
  async ({ portfolioId, content }: { portfolioId: string; content: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await portfolioAPI.addComment(portfolioId, content);
      // console.log("comm response", response)
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to add comment');
    }
  }
);

export const fetchPortfolioComments = createAsyncThunk(
  'portfolio/fetchComments',
  async ({portfolioId, userId}: {portfolioId: string, userId: string}, { rejectWithValue }) => {
    try {
      const response = await portfolioAPI.getComments(portfolioId, userId);
      // console.log("ftcg response",)
      return response.data.data == null ? [] : response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch comments');
    }
  }
);

// Add these new thunks
export const addToCollection = createAsyncThunk(
    'portfolio/addToCollection',
    async (portfolioId: string, { rejectWithValue }) => {
      try {
        const response = await portfolioAPI.addToCollection(portfolioId);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to add to collection');
      }
    }
  );

  export const fetchPortfoliosPage = createAsyncThunk(
    'portfolio/fetchPortfoliosPage',
    async ({ page, limit = 20 }: { page: number; limit?: number }) => {
      const response = await api.get(`/portfolio/feed?page=${page}&limit=${limit}`);
      const portfolios = response.data.data.portfolios.map((p: any) => ({
        ...p,
        hasLiked: p.has_liked
      }));
      return {
        portfolios,
        hasMore: response.data.data.hasMore,
        page,
      };
    }
  );
  
  export const setPortfolioAsPhoto = createAsyncThunk(
    'portfolio/setAsPhoto',
    async ({ portfolioId, type }: { portfolioId: string; type: 'profile' | 'cover' }, { rejectWithValue }) => {
      try {
        const response = await portfolioAPI.setAsPhoto(portfolioId, type);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to set as photo');
      }
    }
  );
  
  export const reportPortfolio = createAsyncThunk(
    'portfolio/report',
    async ({ portfolioId, reason }: { portfolioId: string; reason: string }, { rejectWithValue }) => {
      try {
        const response = await portfolioAPI.reportContent(portfolioId, reason);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to report content');
      }
    }
  );

export const incrementPortfolioViews = createAsyncThunk(
  'portfolio/incrementViews',
  async (portfolioId: string, { rejectWithValue }) => {
    try {
      const response = await portfolioAPI.incrementViews(portfolioId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to increment views');
    }
  }
);

export const checkPortfolioLiked = createAsyncThunk(
    'portfolio/checkLiked',
    async (portfolioId: string, { rejectWithValue }) => {
      try {
        const response = await portfolioAPI.checkLiked(portfolioId);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to check like status');
      }
    }
  );

export const sharePortfolio = createAsyncThunk(
  'portfolio/share',
  async (portfolioId: string, { rejectWithValue }) => {
    try {
      const response = await portfolioAPI.incrementShares(portfolioId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to track share');
    }
  }
);

// Add to portfolioSlice.ts
export const fetchCommentsPage = createAsyncThunk(
  'portfolio/fetchCommentsPage',
  async ({ portfolioId, userId, page = 1, limit = 50 }: { portfolioId: string; userId: string; page?: number; limit?: number }) => {
    const response = await api.get(`/portfolio/${portfolioId}/comments?page=${page}&limit=${limit}&user_id=${userId}`);
    return {
      portfolioId: portfolioId,
      comments: response.data.data.comments,
      hasMore: response.data.data.hasMore,
      page,
    };
  }
);



export const fetchCommentRepliesPage = createAsyncThunk(
  'portfolio/fetchCommentRepliesPage',
  async ({ commentId, userId, page = 1, limit = 5 }: { commentId: string; userId: string;  page?: number; limit?: number }) => {
    const response = await api.get(`/portfolio/comments/${commentId}/replies?page=${page}&limit=${limit}&user_id=${userId}`);
    return {
      commentId,
      replies: response.data.data.replies,
      hasMore: response.data.data.hasMore,
      page,
    };
  }
);

// Add new thunks
export const toggleCommentLike = createAsyncThunk(
  'portfolio/toggleCommentLike',
  async ({ commentId, parentCommentId, isReply }: { commentId: string; parentCommentId: string; isReply: boolean }, {rejectWithValue }) => {
    try {
      const response = await portfolioAPI.toggleCommentLike(commentId);
      return { commentId, parentCommentId, ...response.data.data , isReply};
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to toggle comment like');
    }
  }
);

export const addCommentReply = createAsyncThunk(
  'portfolio/addCommentReply',
  async ({ commentId, content }: { commentId: string; content: string }, { rejectWithValue }) => {
    try {
      const response = await portfolioAPI.addCommentReply(commentId, content);
      return { commentId, reply: response.data.data.reply };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to add reply');
    }
  }
);

export const fetchCommentReplies = createAsyncThunk(
'portfolio/fetchCommentReplies',
  async ({commentId, userId} : {commentId: string, userId: string}, { rejectWithValue }) => {
    try {
      const response = await portfolioAPI.getCommentReplies(commentId, userId);
      return { commentId, replies: response.data.data.replies || [] };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch replies');
    }
  }
);

export const deleteComment = createAsyncThunk(
  'portfolio/deleteComment',
  async ({ commentId, portfolioId }: { commentId: string; portfolioId: string }, { dispatch }) => {
    const response = await api.delete(`/portfolio/comments/${commentId}`);
    return { commentId, portfolioId };
  }
);

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    clearComments: (state) => {
      state.comments = [];
    },
    toggleReplyVisibility: (state, action: PayloadAction<{commentId: string, portfolioId: string}>) => {
      const comment = state.comments.find(c => c.id === action.payload.commentId);
      if(comment == undefined) {
        return
      }

      const portfolioComment = state.portfolioComments[action.payload.portfolioId].find(c => c.id === action.payload.commentId);

      if (comment) {
        comment.show_replies = !comment.show_replies;
      }

      if (portfolioComment) {
        portfolioComment.show_replies = !portfolioComment.show_replies;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolioComments.pending, (state) => {
        state.commentsLoading = true;
      })
      .addCase(fetchPortfolioComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments = action.payload;
      })
      .addCase(fetchPortfolioComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.comments = []
        state.error = action.payload as string;
      })
      .addCase(addPortfolioComment.fulfilled, (state, action) => {
        state.comments.unshift(action.payload);
        if (state.portfolioComments[action.payload.portfolio_id]) {
            state.portfolioComments[action.payload.portfolio_id].unshift(action.payload)
        }
        
        // Update allPortfolios count
        const item = state.allPortfolios.find((p) => p.id === action.payload.portfolio_id);
        if (item) {
          item.comments_count += 1;
        }
      })
      .addCase(addToCollection.fulfilled, (state, action) => {
        toast.success('Added to collection successfully!');
      })
      .addCase(setPortfolioAsPhoto.fulfilled, (state, action) => {
        // toast.success('Profile photo updated successfully!');
      })
      .addCase(reportPortfolio.fulfilled, (state, action) => {
        // toast.success('Content reported successfully');
      })
      .addCase(toggleCommentLike.fulfilled, (state, action) => {
        const { commentId, parentCommentId, liked, isReply } = action.payload;

        // console.log( { commentId, parentCommentId, liked, isReply })
  
        if (isReply) {
          const parentComment = state.comments.find(c => c.id === parentCommentId);
          if (parentComment) {
             const comment = parentComment.replies?.find(c => c.id === commentId);
             if (comment) {
                comment.has_liked = liked;
                comment.like_count = liked ? (comment.like_count || 0) + 1 : Math.max(0, (comment.like_count || 0) - 1);
             }
          }

          // Update in portfolioComments if exists
          if (parentComment && state.portfolioComments[parentComment.portfolio_id]) {
             const portfolioParentComment = state.portfolioComments[parentComment.portfolio_id].find(c => c.id === parentCommentId);
             if (portfolioParentComment) {
                const portfolioComment = portfolioParentComment.replies?.find(c => c.id === commentId);
                if (portfolioComment) {
                    portfolioComment.has_liked = liked;
                    portfolioComment.like_count = liked ? (portfolioComment.like_count || 0) + 1 : Math.max(0, (portfolioComment.like_count || 0) - 1);
                }
             }
          }

        } else {
          const commentItem = state.comments.find(c => c.id === commentId);
          
          if (commentItem) {
             commentItem.has_liked = liked;
             commentItem.like_count = liked ? (commentItem.like_count || 0) + 1 : Math.max(0, (commentItem.like_count || 0) - 1);
             
             // Update in portfolioComments if exists
             if (state.portfolioComments[commentItem.portfolio_id]) {
                const portfolioCommentItem = state.portfolioComments[commentItem.portfolio_id].find(c => c.id === commentId);
                if (portfolioCommentItem) {
                    portfolioCommentItem.has_liked = liked;
                    portfolioCommentItem.like_count = liked ? (portfolioCommentItem.like_count || 0) + 1 : Math.max(0, (portfolioCommentItem.like_count || 0) - 1);
                }
             }
          }
        }
      })
      
      .addCase(addCommentReply.fulfilled, (state, action) => {
        const { commentId, reply } = action.payload;
        const comment = state.comments.find(c => c.id === commentId);

        if(comment == undefined){
          return
        }

        const commentsForPortfolio = state.portfolioComments[comment.portfolio_id] || [];

        const portfolioComment = commentsForPortfolio.find(c => c.id === commentId);

        if (!portfolioComment) {
          return; // or handle missing case
        }
        
        if (comment) {
          const portfolioId = comment.portfolio_id
          if (!comment.replies) {
            comment.replies = [];
            portfolioComment.replies = []
          }
          comment.replies.push(reply);
          comment.reply_count = (comment.reply_count || 0) + 1;
          comment.show_replies = true;

          portfolioComment.replies.push(reply);
          portfolioComment.reply_count = (portfolioComment.reply_count || 0) + 1;
          portfolioComment.show_replies = true;

          // Update portfolio comments count
          const portfolioItem = state.allPortfolios.find(p => p.id === portfolioId);
          if (portfolioItem) {
            portfolioItem.comments_count = (portfolioItem.comments_count || 0) + 1;
          }
        }
      })
      
      .addCase(fetchCommentReplies.fulfilled, (state, action) => {
        const { commentId, replies } = action.payload;
        const comment = state.comments.find(c => c.id === commentId);
        if (comment) {
          comment.replies = replies;
          comment.show_replies = true;
        }
      })
      .addCase(fetchCommentsPage.pending, (state) => {
        state.commentsLoading = true;
      })
      .addCase(fetchCommentsPage.fulfilled, (state, action) => {
        const { portfolioId, comments, hasMore, page } = action.payload;
        const finalComments = comments == null ? [] : comments
        if (page === 1) {
          state.comments = finalComments
        } else {
          state.comments.push(...finalComments);
        }
        
        state.hasMoreComments = hasMore;
        state.commentsPage = page;
        state.commentsLoading = false;
        state.portfolioComments[portfolioId] = state.comments
      })
      .addCase(fetchCommentRepliesPage.fulfilled, (state, action) => {
        const { commentId, replies, hasMore, page } = action.payload;
        
        const comment = state.comments.find(c => c.id === commentId);
        if (comment == undefined) {
          return
        }

        const portfolioComment = state.portfolioComments[comment.portfolio_id].find(c => c.id === commentId)
        if (portfolioComment == undefined) {
          return
        }

        if (page === 1) {
          comment.replies = replies;
          portfolioComment.replies = replies;
        } else {
          comment.replies = [...(comment.replies || []), ...replies];
          portfolioComment.replies = [...(portfolioComment.replies || []), ...replies];
        }
        comment.show_replies = true;
        portfolioComment.show_replies = true;
        
        state.hasMoreReplies[commentId] = hasMore;
        state.repliesPage[commentId] = page;
        state.repliesLoading[commentId] = false;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { commentId, portfolioId } = action.payload;
        
        let countToDeduct = 1;
        
        // Check if it's a main comment to adjust count correctly
        const mainComment = state.comments.find(c => c.id === commentId) || 
                            (state.portfolioComments[portfolioId]?.find(c => c.id === commentId));
        
        if (mainComment) {
            countToDeduct = 1 + (mainComment.reply_count || 0);
        }

        // Helper to remove comment from an array
        const removeComment = (list: PortfolioComment[]) => {
          const idx = list.findIndex(c => c.id === commentId);
          if (idx !== -1) {
            list.splice(idx, 1);
          }
        };

        // Remove from main comments
        removeComment(state.comments);

        // Remove from portfolioComments map
        if (state.portfolioComments[portfolioId]) {
             removeComment(state.portfolioComments[portfolioId]);
        }

        // Update allPortfolios count
        const item = state.allPortfolios.find((p) => p.id === portfolioId);
        if (item) {
          item.comments_count = Math.max(0, item.comments_count - countToDeduct);
        }
        
        // Helper to remove reply from comments
        const removeReply = (list: PortfolioComment[]) => {
            list.forEach(comment => {
                if (comment.replies) {
                  const initialLength = comment.replies.length;
                  comment.replies = comment.replies.filter(reply => reply.id !== commentId);
                  if (comment.replies.length < initialLength) {
                       comment.reply_count = Math.max(0, comment.reply_count - 1);
                       comment.show_replies = true; // Keep replies open to show it's gone
                  }
                }
              });
        }

        // Remove from replies in main comments
        removeReply(state.comments);

        // Remove from replies in portfolioComments map
        if (state.portfolioComments[portfolioId]) {
            removeReply(state.portfolioComments[portfolioId]);
        }
      })   
      .addCase(fetchPortfoliosPage.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPortfoliosPage.fulfilled, (state, action) => {
        const { portfolios, hasMore, page } = action.payload;
        
        if (page === 1) {
          // First page - replace all
          state.allPortfolios = portfolios;
        } else {
          // Subsequent pages - append
          state.allPortfolios = [...state.allPortfolios, ...portfolios];
        }
        
        state.hasMorePortfolios = hasMore;
        state.currentPage = page;
        state.loading = false;
      })
      .addCase(fetchPortfoliosPage.rejected, (state) => {
        state.loading = false;
        state.hasMorePortfolios = false; // Stop infinite scroll on error
      })
      .addCase(togglePortfolioLike.fulfilled, (state, action) => {
        const { is_liked } = action.payload;
        const id = action.meta.arg;
        const item = state.allPortfolios.find((p) => p.id === id);
        if (item) {
          item.hasLiked = is_liked;
          if (is_liked) {
             item.likes = (item.likes || 0) + 1;
          } else {
             item.likes = Math.max(0, (item.likes || 1) - 1);
          }
        }
      });
  },
});

export const { clearComments, toggleReplyVisibility } = portfolioSlice.actions;
export default portfolioSlice.reducer;
